import { IconPlus } from '@developerz/ui';
import { useQueryClient } from '@tanstack/solid-query';
import { createMemo, createSignal, For, type JSX, Show } from 'solid-js';
import { AddWebhookModal } from '../components/AddWebhookModal';
import { DeliveryLog } from '../components/DeliveryLog';
import { EmptyState } from '../components/EmptyState';
import { ErrorState } from '../components/ErrorState';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { QueryBoundary } from '../components/QueryBoundary';
import { Skeleton } from '../components/Skeleton';
import { Toast } from '../components/Toast';
import { Button } from '../components/ui/Button';
import { WebhookCard } from '../components/WebhookCard';
import { t } from '../i18n';
import { createKeyedMutation } from '../lib/keyed-mutation';
import { createMutation } from '../lib/mutation';
import { SESSION_TOKEN, useMe } from '../lib/session';
import { createToast } from '../lib/toast';
import {
  canManageWebhooks,
  createWebhook,
  deleteWebhook,
  toCardRows,
  toDeliveryRows,
  useWebhookDeliveries,
  useWebhooks,
  WEBHOOK_DELIVERIES_QUERY_KEY,
} from '../lib/webhooks';
import { createWebhookTest } from '../lib/webhookTest';

// Named webhooks + delivery telemetry (#807). CRUD + test-send live in apps/api;
// the per-card health stats are derived from the one deliveries read (see
// lib/webhooks). Token-gated like the other account surfaces; the create form's
// one-time signing secret is shown once, on create, then never again.
//
// THE TWO ROW WRITES (plan 101 slice 17 row 17-7) were bare `void x().then(...)`:
// a refused delete became a window `unhandledrejection` (reported as a client
// crash) while the screen said nothing, Delete fired on one click, a double
// click sent two signed deliveries, and the test-send's verdict was dropped.
// Now: Delete asks first (the card's confirm) and runs through a keyed mutation
// whose refusal toasts the API's own `detail`; Send test is `createWebhookTest`,
// the repo tab's per-binding lock and graded outcome. Both are withheld from a
// caller who would plainly 403 (`write:webhooks` is owner-only), and both
// re-read either way.

export type WebhookHealth = 'healthy' | 'retrying' | 'unhealthy';

export type WebhookRow = {
  readonly id: string;
  readonly name: string;
  readonly url_masked: string;
  readonly events: readonly string[];
  readonly last_status_code: number | null;
  readonly health: WebhookHealth;
  /**
   * 24h success rate, or `null` when NO resolved delivery backs it — the ledger
   * read has not landed, or this endpoint has never fired. Nullable rather than
   * defaulted to 100 because this is a health surface: the default is the one
   * answer a reader acts on, and it was being served from no evidence at all.
   */
  readonly success_pct: number | null;
  /** Human-relative last-delivery time (e.g. "2s ago"). */
  readonly last_delivery: string;
  /** Payloads currently in the retry queue, when health is `retrying`. */
  readonly retrying?: number;
};

export type Delivery = {
  readonly id: string;
  /** Display timestamp, e.g. "14:02:45.120". */
  readonly at: string;
  readonly event_id: string;
  readonly type: string;
  /** null while the attempt is still pending (queued, not yet answered). */
  readonly status_code: number | null;
  /** Round-trip latency — not tracked server-side, so absent for live rows. */
  readonly latency_ms?: number;
  /** Pretty-printed JSON request body. */
  readonly payload: string;
};

export function AccountWebhooks(props: { token?: string | null }): JSX.Element {
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const qc = useQueryClient();
  const webhooks = useWebhooks(token);
  const deliveries = useWebhookDeliveries(token);

  const [addOpen, setAddOpen] = createSignal(false);
  const [newSecret, setNewSecret] = createSignal<{ name: string; secret: string } | null>(null);

  // `deliveries.data` is passed through UNDEFENDED on purpose: `?? []` here is
  // what let a failed ledger read paint every endpoint 100% healthy. `toCardRows`
  // reads `undefined` as "no evidence" and drops the fields that need it.
  const rows = createMemo<readonly WebhookRow[]>(() =>
    toCardRows(webhooks.rows(), deliveries.data, t('account_webhooks.never')),
  );
  const deliveryRows = createMemo(() => toDeliveryRows(deliveries.data ?? []));

  // `reload()`, not an invalidation, for the paged list: the walk keys its query
  // at the LAST page, so invalidating would re-read that page and keep page one's
  // pre-write rows (`lib/paged.ts`).
  const refresh = (): void => {
    webhooks.reload();
    void qc.invalidateQueries({ queryKey: [WEBHOOK_DELIVERIES_QUERY_KEY] });
  };

  const onCreate = createMutation({
    call: (body: { name: string; url: string; events: string[] }) => createWebhook(body, token),
    fallback: t('account_webhooks.create_failed'),
    onSuccess: (created) => {
      setNewSecret({ name: created.name, secret: created.secret });
      setAddOpen(false);
      refresh();
    },
  });

  const me = useMe(token);
  // Shut until `/v1/me` lands (mid-load `role` is undefined, the shape of the PAT
  // that KEEPS the controls); the server stays the authority.
  const canWrite = (): boolean => !me.isLoading && canManageWebhooks(me.data?.role);
  const { toast, notify, dismiss } = createToast();
  // Every outcome notifies, so the re-read rides the notice: the attempt the
  // delivery log shows is the one just made, success or refusal.
  const test = createWebhookTest({
    token,
    notify: (message, tone) => {
      notify(message, tone);
      refresh();
    },
  });
  const removing = createKeyedMutation({
    fallback: t('account_webhooks.delete_failed'),
    onError: (message) => {
      notify(message, 'error');
      refresh();
    },
  });
  const onDelete = (id: string): void =>
    void removing.run(id, async () => {
      await deleteWebhook(id, token);
      refresh();
    });

  return (
    <>
      <PageHeader
        title={t('account_webhooks.title')}
        desc={t('account_webhooks.subtitle')}
        actions={
          <Show when={token !== null}>
            <Button
              onClick={() => {
                onCreate.clearError();
                setAddOpen(true);
              }}
            >
              <IconPlus />
              {t('account_webhooks.add_webhook')}
            </Button>
          </Show>
        }
      />

      <Show
        when={token !== null}
        fallback={<EmptyState message={t('account_webhooks.signed_out')} />}
      >
        <Show when={newSecret()}>
          {(secret) => (
            <div class="alert webhook-secret-notice" role="status">
              <p class="alert__title">
                {t('account_webhooks.secret_once', { name: secret().name })}
              </p>
              <code class="code-block webhook-secret-notice__value">{secret().secret}</code>
              <Button variant="outline" size="sm" onClick={() => setNewSecret(null)}>
                {t('account_webhooks.dismiss')}
              </Button>
            </div>
          )}
        </Show>

        {/* QueryBoundary, not a bare Show: loading must not read as "empty". */}
        <QueryBoundary
          query={webhooks.view}
          isEmpty={() => rows().length === 0}
          loading={<Skeleton height="16rem" />}
          empty={<EmptyState message={t('account_webhooks.empty')} />}
        >
          {() => (
            <div class="account-webhooks__stack">
              <Panel title={t('account_webhooks.overview_title')}>
                <ul class="row-list">
                  <For each={rows()}>
                    {(row) => (
                      <WebhookCard
                        row={row}
                        busy={test.isTesting(row.id) || removing.isBusy(row.id)}
                        {...(canWrite() ? { onSendTest: test.run, onDelete } : {})}
                      />
                    )}
                  </For>
                </ul>
              </Panel>

              {/* A FAILED ledger read is not "nothing has fired yet". The panel
                  used to be gated on row count alone, so a 500 made it vanish
                  and took the card stats' evidence with it silently. */}
              <Show when={deliveries.isError}>
                <Panel title={t('account_webhooks.deliveries_title')}>
                  <ErrorState
                    error={deliveries.error}
                    message={t('account_webhooks.deliveries_unavailable')}
                    onRetry={() => void deliveries.refetch()}
                  />
                </Panel>
              </Show>

              <Show when={!deliveries.isError && deliveryRows().length > 0}>
                <Panel title={t('account_webhooks.deliveries_title')}>
                  <DeliveryLog deliveries={deliveryRows()} />
                </Panel>
              </Show>
            </div>
          )}
        </QueryBoundary>
      </Show>

      <Toast
        message={toast()?.message ?? null}
        tone={toast()?.tone ?? 'success'}
        onDismiss={dismiss}
      />
      <AddWebhookModal
        open={addOpen()}
        pending={onCreate.pending()}
        error={onCreate.error()}
        onCreate={(body) => void onCreate.run(body)}
        onClose={() => setAddOpen(false)}
      />
    </>
  );
}
